import { useState } from "react";
import * as v from "valibot";
import { valibotResolver } from "@hookform/resolvers/valibot";
import styles from "./addProduct.module.css";
import { Button, Col, Container, Form, Row } from "react-bootstrap";
import { Input } from "../common/input/Input";
import { useForm } from "react-hook-form";
import { CustomToast } from "../common/customToast/CustomToast";
import { Select } from "../common/select/Select";
import { useAddProductMutation } from "../../features/api/products/productsApi";
import { useGetAllCategoryQuery } from "../../features/api/category/categoryApi";
const schema = v.object({
  name: v.pipe(
    v.string("The product name is required"),
    v.minLength(3, "Must be at least 3 characters long")
  ),
  intakePrice: v.pipe(
    v.number("The purchase price is required"),
    v.minValue(0.01, "The price must be greater than zero")
  ),
  sellingPrice: v.pipe(
    v.number("The selling price is required"),
    v.minValue(0.01, "The price must be greater than zero")
  ),
  unit: v.pipe(
    v.number("The unit/pack is required"),
    v.minValue(1, "Must be at least 1")
  ),
  manufacturer: v.string("The manufacturer is required"),
  size: v.string("The size of the product is required"),
  image: v.optional(v.unknown()),
  country: v.string("The country of origin is required"),
  dimension: v.string("The dimension is required"),
  category: v.pipe(
    v.string("Category is required"),
    v.minLength(2, "Category is required")
  ),
  weight: v.pipe(
    v.number("Weight is required"),
    v.minValue(1, "Weight cannot be negative or zero")
  ),
});

export const AddProduct = () => {
  const [product, setProduct] = useState({
    name: "",
    intakePrice: "",
    sellingPrice: "",
    unit: "",
    size: "",
    manufacturer: "",
    country: "",
    dimension: "",
    category: "",
    weight: "",
    image: "",
  });
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: product,
    resolver: valibotResolver(schema),
  });
  const { data, isCategoryLoading, isCategoryLoadingError } =
    useGetAllCategoryQuery();
  const [addProduct, { isLoading, isSuccess, isError, error }] =
    useAddProductMutation();
  const onSubmit = async (data) => {
    const productData = new FormData();
    productData.append(
      "productRequestDto",
      JSON.stringify({
        name: data.name,
        unit: data.unit,
        origin: data.country,
        category: data.category,
        dimension: data.dimension,
        manufacturer: data.manufacturer,
        weight: data.weight,
        intakePrice: data.intakePrice,
        sellingPrice: data.sellingPrice,
        size: data.size,
      })
    );

    if (data.image && data.image.length > 0) {
      productData.append("image", data.image[0]); // Extract the first file
    }
    try {
      await addProduct(productData).unwrap();
    } catch (err) {}
    // Perform API request to update product -
    reset();
  };

  return (
    <div className="mb-5">
      <Container className="mt-5 shadow-sm">
        {isSuccess && (
          <CustomToast msg={"Product added/updated successfully!"} />
        )}
        {isError && <CustomToast err={error} msg={error.data.errorMessage} />}
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Row>
            <Col lg={6} md={6} xs={12}>
              <Input
                label={"Name"}
                field={"name"}
                register={register}
                defaultValue={product.name}
                errors={errors}
              />
              <Input
                label={"Intake Price"}
                type={"number"}
                withUnit={true}
                field={"intakePrice"}
                register={register}
                defaultValue={product.intakePrice}
                errors={errors}
              />
              <Input
                label={"Selling Price"}
                type={"number"}
                withUnit={true}
                field={"sellingPrice"}
                register={register}
                defaultValue={product.sellingPrice}
                errors={errors}
              />
              <Input
                label={"Stock"}
                type={"number"}
                field={"unit"}
                register={register}
                defaultValue={product.unit}
                errors={errors}
              />
              <Input
                label={"Size / Unit"}
                field={"size"}
                register={register}
                defaultValue={product.size}
                errors={errors}
              />

              <Form.Group className="mb-3">
                <Form.Label>Image</Form.Label>
                <Form.Control
                  type="file"
                  accept="image/*"
                  {...register("image", { required: "Image is required" })}
                  onChange={(e) =>
                    setProduct({ ...product, image: e.target.files[0] })
                  }
                />
                {errors.image && (
                  <p className="text-danger">{errors.image.message}</p>
                )}
              </Form.Group>
            </Col>

            <Col lg={6} md={6} xs={12}>
              <Input
                label={"Manufacturer"}
                field={"manufacturer"}
                register={register}
                defaultValue={product.manufacturer}
                errors={errors}
              />
              <Input
                label={"Origin"}
                field={"country"}
                register={register}
                defaultValue={product.country}
                errors={errors}
              />
              <Input
                label={"Dimension"}
                field={"dimension"}
                register={register}
                defaultValue={product.dimension}
                errors={errors}
              />
              <Input
                label={"Weight/Pack"}
                type={"number"}
                withUnit={true}
                unit={"g/ml"}
                field={"weight"}
                register={register}
                defaultValue={product.weight}
                errors={errors}
              />
              <Select
                label="Category"
                field="category"
                register={register}
                options={
                  isCategoryLoading
                    ? [
                        {
                          value: "",
                          label: "Loading categories...",
                        },
                      ]
                    : isCategoryLoadingError
                    ? [{ value: "", label: "Failed to load categories" }]
                    : data?.map((cat) => ({
                        value: cat.name,
                        label: cat.name,
                      })) ?? []
                }
                errors={errors}
              />
            </Col>
          </Row>
          <Row className="py-2">
            <Col lg={12}>
              <Button
                type="submit"
                variant="success"
                className="w-100"
                disabled={
                  isLoading || isCategoryLoading || isCategoryLoadingError
                }
              >
                {isLoading ? "Saving" : "Submit"}
              </Button>
            </Col>
          </Row>
        </Form>
      </Container>
    </div>
  );
};
